/* eslint-disable react/prop-types */
import React from 'react';

/**
 * Fallback UIs for the three boundary tiers.
 *
 * These render while the app is already broken, so they import no other
 * component. A fallback built out of the same design-system primitives that
 * just threw would throw again — and a fallback that throws propagates to the
 * next boundary up, which is exactly the blank screen we're removing.
 * Plain elements and Tailwind class strings only.
 */

const IS_DEV = process.env.NODE_ENV !== 'production';

function WarningIcon({
    className
}) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    );
}

/**
 * Names the record that failed so the user knows WHICH row to fix or delete,
 * and so a screenshot of the error is enough to reproduce it.
 */
export function RecordCaption({
    recordType, recordId, recordLabel
}) {
    if (!recordId && !recordLabel) return null;
    return (
        <p className="mt-1 text-xs text-muted-foreground">
            {recordType ? `${recordType}: ` : ''}
            {recordLabel ? <span className="font-medium">{recordLabel}</span> : null}
            {recordId ? <code className="ml-1 rounded bg-black/5 px-1 py-0.5 dark:bg-white/10">{recordId}</code> : null}
        </p>
    );
}

function DevDetails({
    error, errorInfo
}) {
    if (!IS_DEV) return null;
    return (
        <details className="mt-3">
            <summary className="cursor-pointer text-xs text-muted-foreground">Technical details</summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-black/5 p-2 text-[11px] leading-snug dark:bg-white/10">
                {String((error && error.stack) || (error && error.message) || error)}
                {errorInfo && errorInfo.componentStack ? `\n${errorInfo.componentStack}` : ''}
            </pre>
        </details>
    );
}

const BTN = 'inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors';
const BTN_PRIMARY = `${BTN} bg-primary text-primary-foreground hover:opacity-90`;
const BTN_GHOST = `${BTN} border border-black/10 hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10`;

/** Tier 1 — last resort. The shell is gone; offer a way back. */
export function AppErrorFallback({
    error, errorInfo
}) {
    return (
        <div role="alert" className="flex min-h-screen w-full items-center justify-center bg-gray-100 p-6 dark:bg-background">
            <div className="w-full max-w-lg rounded-lg border border-black/10 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-card">
                <div className="flex items-start gap-3">
                    <WarningIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <div className="min-w-0">
                        <h1 className="text-base font-semibold">Something went wrong</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            BeX hit an unexpected error and could not finish loading this page.
                            Your saved work is unaffected.
                        </p>
                        <DevDetails error={error} errorInfo={errorInfo} />
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button type="button" className={BTN_PRIMARY} onClick={() => window.location.reload()}>
                                Reload
                            </button>
                            <a className={BTN_GHOST} href="/organizations">Back to organizations</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Tier 2 — the page failed; navigation and the shell around it are alive. */
export function RouteErrorFallback({
    error, errorInfo, reset, recordType, recordId, recordLabel
}) {
    return (
        <div role="alert" className="flex h-full w-full flex-1 items-start justify-center p-6">
            <div className="w-full max-w-2xl rounded-lg border border-black/10 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-card">
                <div className="flex items-start gap-3">
                    <WarningIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold">This page couldn&apos;t be displayed</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                            The rest of BeX still works — use the menu to go somewhere else, or try again.
                        </p>
                        <RecordCaption recordType={recordType} recordId={recordId} recordLabel={recordLabel} />
                        <DevDetails error={error} errorInfo={errorInfo} />
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button type="button" className={BTN_PRIMARY} onClick={reset}>Try again</button>
                            <button type="button" className={BTN_GHOST} onClick={() => window.location.reload()}>
                                Reload page
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Tier 3 — one panel failed; the rest of the builder stays editable. */
export function PanelErrorFallback({
    error, errorInfo, reset, title, recordType, recordId, recordLabel
}) {
    return (
        <div
            role="alert"
            className="rounded-md border border-amber-300/70 bg-amber-50 p-3 text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-100"
        >
            <div className="flex items-start gap-2">
                <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{title || 'This section could not be displayed'}</p>
                    <p className="mt-1 text-xs opacity-80">
                        It is most likely a malformed saved record. Everything else on this
                        screen still works, so you can edit or delete it.
                    </p>
                    <RecordCaption recordType={recordType} recordId={recordId} recordLabel={recordLabel} />
                    <DevDetails error={error} errorInfo={errorInfo} />
                    <button
                        type="button"
                        onClick={reset}
                        className="mt-3 rounded border border-amber-500/40 px-2 py-1 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/10"
                    >
                        Try again
                    </button>
                </div>
            </div>
        </div>
    );
}
